import { COLOR } from 'const/color';
import { Dayjs } from 'dayjs';
import { useFetchMyCourierUser } from 'hooks/useFetchUsers';
import React, { ElementType, useState } from 'react';
import { FaSuitcase } from 'react-icons/fa';
import {
  MdNotInterested,
  MdThumbDown,
  MdThumbsUpDown,
  MdThumbUp,
} from 'react-icons/md';
import Swal, { SweetAlertIcon, SweetAlertResult } from 'sweetalert2';
import Button from '../../../components/buttons/Button';
import { Loader } from '../../../components/Loader/Loader';
import { ERROR } from 'const/error';
import { ReqEnum } from '../../../const/requestStatus';
import {
  ExtraWorkConst,
  ShiftCourierJsonForCourier,
  ShiftCourierWithDateJson,
  ShiftStatusType,
} from '../../../const/ShiftConst';
import { Region } from '../../../types/orderTypes';
import {
  NotAvailableShift,
  NotAvailableShiftUnRegister,
} from '../AddWishSpotButton/AddWishSpotButton';
import { makeUpdatedShiftStatusForRequestedCourier } from '../updateShift';
import { WeekDayBox } from '../WeekDayForAdmin/WeekDayForAdmin';
import './WeekDayForCourier.scss';

function NormalBookedShift(): JSX.Element {
  return (
    <>
      <div className="courierBox">
        <div
          style={{ backgroundColor: COLOR.SHIFT_GREEN, color: COLOR.WHITE }}
          className="courierNameBox"
        >
          <div className="addCourierBtn">
            <MdThumbUp /> {`Confirmed`}
          </div>
        </div>
      </div>
    </>
  );
}

function BookedShiftWithSorting(): JSX.Element {
  return (
    <React.Fragment>
      <div className="bookedShiftWithSorting-box">
        <div className="courierNameBoxWithSort">
          <div className="addCourierBtn">
            <MdThumbUp /> {`Confirmed`}
          </div>
        </div>
        <div className="bookedSortShiftInfoBox">{`➕  Sorting`}</div>
      </div>
    </React.Fragment>
  );
}

function BookedShift({
  courier,
}: {
  courier: ShiftCourierJsonForCourier;
}): JSX.Element {
  const extraWorks = courier.extraWork.filter(
    obj => obj.typeOfExtraWork === ExtraWorkConst.SORT.typeOfExtraWork
  );
  const isSortingShift = extraWorks.length > 0;

  // todo: send in the sort type into component to show it
  return isSortingShift ? <BookedShiftWithSorting /> : <NormalBookedShift />;
}

function RequestedShift({
  UpdateRequestedShiftButtons,
  shiftCourierList,
  courierId,
  courierName,
  shiftCourier,
}: {
  UpdateRequestedShiftButtons: ElementType;
  shiftCourierList: ShiftCourierJsonForCourier[];
  courierId: number;
  courierName: string;
  shiftCourier: ShiftCourierJsonForCourier;
}) {
  return (
    <>
      <div className="courierBox">
        <div
          style={{ backgroundColor: COLOR.SHIFT_YELLOW, color: COLOR.BLACK }}
          className="courierNameBox"
        >
          <div className="addCourierBtn">
            <MdThumbsUpDown />
            {'Accept or decline shift?'}
            <UpdateRequestedShiftButtons
              shiftCourierList={shiftCourierList}
              shiftCourier={shiftCourier}
              courierId={courierId}
              courierName={courierName}
            />
          </div>
        </div>
      </div>
    </>
  );
}

/* {'Accept requested work'} */

function OpenShift({
  UpdateShiftModal,
  shiftCourierList,
  courierId,
  courierName,
  shiftCourier,
}: {
  UpdateShiftModal: (
    shiftCourier: ShiftCourierJsonForCourier,
    shiftCourierList: ShiftCourierJsonForCourier[],
    courierId: number,
    courierName: string
  ) => void;
  shiftCourierList: ShiftCourierJsonForCourier[];
  courierId: number;
  courierName: string;
  shiftCourier: ShiftCourierJsonForCourier;
}) {
  return (
    <>
      <div className="courierBox">
        <div
          style={{ backgroundColor: COLOR.BLACK, color: COLOR.WHITE }}
          onClick={() =>
            UpdateShiftModal(
              shiftCourier,
              shiftCourierList,
              courierId,
              courierName
            )
          }
          className="courierNameBox"
        >
          <div className="addCourierBtn">
            <FaSuitcase /> Book open spot
          </div>
        </div>
      </div>
    </>
  );
}

function DeclinedShift() {
  return (
    <>
      <div className="courierBox">
        <div
          style={{ backgroundColor: COLOR.SHIFT_RED, color: COLOR.WHITE }}
          className="courierNameBox"
        >
          <div className="addCourierBtn">
            <MdThumbDown /> You declined
          </div>
        </div>
      </div>
    </>
  );
}

function CanceledShift() {
  return (
    <>
      <div className="courierBox">
        <div
          style={{ backgroundColor: COLOR.PROGRESS_RED, color: COLOR.WHITE }}
          className="courierNameBox"
        >
          <div className="addCourierBtn">
            <MdNotInterested /> Your shift has been canceled
          </div>
        </div>
      </div>
    </>
  );
}

function isNoAnswer(dismiss: Swal.DismissReason): boolean {
  const options = [Swal.DismissReason.cancel, Swal.DismissReason.close];
  return options.includes(dismiss);
}

export async function updateShift(
  shiftCourier: ShiftCourierJsonForCourier,
  shiftCourierList: ShiftCourierJsonForCourier[],
  courierId: number,
  courierName: string,
  updateWithStatus: ShiftStatusType
): Promise<ShiftCourierJsonForCourier[]> {
  const newShiftCourierList: ShiftCourierJsonForCourier[] = await Promise.all(
    shiftCourierList.map(
      (shiftCourierObj: ShiftCourierJsonForCourier, index: number) => {
        if (shiftCourier.id === shiftCourierObj.id) {
          const shiftCourierPromise: Promise<ShiftCourierJsonForCourier> =
            makeUpdatedShiftStatusForRequestedCourier(
              shiftCourierObj,
              courierName,
              updateWithStatus,
              courierId
            );

          return shiftCourierPromise;
        } else {
          return shiftCourierObj;
        }
      }
    )
  );

  return newShiftCourierList;
}

export function WeekDayForCourier({
  courierListBookedObj,
  date,
  region,
}: {
  courierListBookedObj: ShiftCourierWithDateJson;
  date: Dayjs;
  region: Region;
}): JSX.Element {
  const [shiftCourierList, setShiftCourierList] = useState<
    ShiftCourierJsonForCourier[]
  >(courierListBookedObj.courierDayBooked);
  const [courierNameObj, fetchcourierNameStatus] = useFetchMyCourierUser();

  // nettwork call to updte the courierList for that date

  async function declineShift(
    courierId: number,
    courierName: string,
    shiftCourier: ShiftCourierJsonForCourier
  ) {
    const newShiftCourierList = await updateShift(
      shiftCourier,
      shiftCourierList,
      courierId,
      courierName,
      ShiftStatusType.DECLINED
    );
    setShiftCourierList(newShiftCourierList);
    const statusOrder = newShiftCourierList.filter(
      shiftCourierObj => shiftCourier.id === shiftCourierObj.id
    )[0].status;

    if (statusOrder !== ShiftStatusType.DELETED) {
      Swal.fire(ERROR.SHIFT_WORK_DECLINED);
    } else {
      Swal.fire(
        'Ops...',
        `The shift was ${statusOrder}, try refreshing the browser to see if there are more open shifts`,
        'warning'
      );
    }
  }

  async function acceptShift(
    courierId: number,
    courierName: string,
    shiftCourier: ShiftCourierJsonForCourier
  ) {
    const newShiftCourierList = await updateShift(
      shiftCourier,
      shiftCourierList,
      courierId,
      courierName,
      ShiftStatusType.BOOKED
    );
    setShiftCourierList(newShiftCourierList);
    const statusOrder = newShiftCourierList.filter(
      shiftCourierObj => shiftCourier.id === shiftCourierObj.id
    )[0].status;

    if (statusOrder !== ShiftStatusType.DELETED) {
      Swal.fire('Success!', 'You are signed up for work!.', 'success');
    } else {
      Swal.fire(
        'Ops...',
        `The shift was ${statusOrder}, try refreshing the browser to see if there are more open shifts`,
        'warning'
      );
    }
  }

  function displayAlertMessage(
    title: string,
    text: string,
    confirmButtonText: string,
    icon: SweetAlertIcon = 'question'
  ): Promise<SweetAlertResult> {
    return Swal.fire({
      title,
      text,
      icon,
      showCancelButton: true,
      confirmButtonText,
      confirmButtonColor: COLOR.SHIFT_GREEN,
      cancelButtonColor: COLOR.PROGRESS_RED,
      cancelButtonText: 'Not sure yet',
      allowOutsideClick: false,
      showCloseButton: true,
    });
  }

  function UpdateRequestedShiftButtons({
    shiftCourier,
    shiftCourierList,
    courierId,
    courierName,
  }: {
    shiftCourier: ShiftCourierJsonForCourier;
    shiftCourierList: ShiftCourierJsonForCourier[];
    courierId: number;
    courierName: string;
  }): JSX.Element {
    async function acceptRequestedShiftModal(
      shiftCourier: ShiftCourierJsonForCourier,
      shiftCourierList: ShiftCourierJsonForCourier[],
      courierId: number,
      courierName: string
    ) {
      try {
        const result = await displayAlertMessage(
          'Commit to work?',
          'Are you sure you want to work this day?',
          'Yes sign me up'
        );
        if (result.value) {
          await acceptShift(courierId, courierName, shiftCourier);
        } else if (isNoAnswer(result.dismiss)) {
          Swal.fire(ERROR.SHIFT_NO_ANSWER);
        }
      } catch (error: any) {
        console.error(error.message);
      }
    }

    async function declineRequestedShiftModal(
      shiftCourier: ShiftCourierJsonForCourier,
      shiftCourierList: ShiftCourierJsonForCourier[],
      courierId: number,
      courierName: string
    ) {
      try {
        const result = await displayAlertMessage(
          'Decline work?',
          'Are you sure want to decline work this day?',
          'Yes decline the work'
        );
        if (result.value) {
          await declineShift(courierId, courierName, shiftCourier);
        } else if (isNoAnswer(result.dismiss)) {
          Swal.fire(ERROR.SHIFT_NO_ANSWER);
        }
      } catch (error: any) {
        console.error(error.message);
      }
    }

    return (
      <div className="btn-group">
        <Button
          className="accept-btn"
          onClick={() =>
            acceptRequestedShiftModal(
              shiftCourier,
              shiftCourierList,
              courierId,
              courierName
            )
          }
          title="Accept"
        ></Button>
        <Button
          className="decline-btn"
          onClick={() =>
            declineRequestedShiftModal(
              shiftCourier,
              shiftCourierList,
              courierId,
              courierName
            )
          }
          title="Decline"
        ></Button>
      </div>
    );
  }

  async function updateOpenShiftModal(
    shiftCourier: ShiftCourierJsonForCourier,
    shiftCourierList: ShiftCourierJsonForCourier[],
    courierId: number,
    courierName: string
  ) {
    try {
      const result = await displayAlertMessage(
        'Commit to work?',
        'Are you sure you want to work this day?',
        'Yes decline the work',
        'warning'
      );

      await Swal.fire({
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes sign me up',
      });

      if (result.value) {
        const newShiftCourierList = await updateShift(
          shiftCourier,
          shiftCourierList,
          courierId,
          courierName,
          ShiftStatusType.BOOKED
        );
        setShiftCourierList(newShiftCourierList);
        const statusOrder = newShiftCourierList.filter(
          shiftCourierObj => shiftCourier.id === shiftCourierObj.id
        )[0].status;

        if (statusOrder !== ShiftStatusType.DELETED) {
          Swal.fire('Success!', 'You are signed up for work!.', 'success');
        } else {
          Swal.fire(
            'Ops...',
            `The shift was ${statusOrder}, try refreshing the browser to see if there are more open shifts`,
            'warning'
          );
        }
      } else {
        Swal.fire(ERROR.SHIFT_NOT_SIGNED_UP);
      }
    } catch (error: any) {
      console.error(error.message);
    }
  }

  if (fetchcourierNameStatus === ReqEnum.SUCCESS && shiftCourierList) {
    const courierId = courierNameObj.id;
    const courierName = `${courierNameObj.firstName} ${courierNameObj.lastName}`;

    const filterCourierList = (statusType: ShiftStatusType) =>
      shiftCourierList.filter(
        shift => shift.courierId === courierId && shift.status === statusType
      );

    const bookedList = filterCourierList(ShiftStatusType.BOOKED);
    const requestedList = filterCourierList(ShiftStatusType.REQUESTED);
    const declinedList = filterCourierList(ShiftStatusType.DECLINED);
    const openList = shiftCourierList.filter(
      shiftCourier => shiftCourier.status === ShiftStatusType.OPEN
    );
    const wishList = filterCourierList(ShiftStatusType.WISH);
    const canceledList = filterCourierList(ShiftStatusType.CANCELED);

    const noOpenList = openList.length === 0;
    const noBookedList = bookedList.length === 0;
    const noRequestedList = requestedList.length === 0;
    const noDeclinedList = declinedList.length === 0;
    const noCanceledList = canceledList.length === 0;
    const noWishList = wishList.length === 0;

    const isShowOpenShift =
      noRequestedList && noBookedList && noDeclinedList && openList.length > 0;
    const isShowBookedShift = bookedList.length > 0;
    const isShowRequestedShift = noBookedList && requestedList.length > 0;
    const isShowDeclinedShift =
      noBookedList && noRequestedList && declinedList.length > 0;
    const isShowCanceledShift =
      noDeclinedList &&
      noBookedList &&
      noRequestedList &&
      canceledList.length > 0;
    const isShowNotAvailableShift =
      noOpenList &&
      noRequestedList &&
      noBookedList &&
      noDeclinedList &&
      noCanceledList;

    return (
      <div className="weekdayForCourier">
        <WeekDayBox date={date} />

        {isShowBookedShift && <BookedShift courier={bookedList[0]} />}
        {isShowRequestedShift && (
          <RequestedShift
            UpdateRequestedShiftButtons={UpdateRequestedShiftButtons}
            shiftCourierList={shiftCourierList}
            courierId={courierId}
            courierName={courierName}
            shiftCourier={requestedList[0]}
          />
        )}
        {isShowDeclinedShift && <DeclinedShift />}
        {isShowOpenShift && openList[0] && (
          <OpenShift
            UpdateShiftModal={updateOpenShiftModal}
            shiftCourierList={shiftCourierList}
            courierId={courierId}
            courierName={courierName}
            shiftCourier={openList[0]}
          />
        )}
        {isShowCanceledShift && <CanceledShift />}
        {isShowNotAvailableShift && noWishList && (
          <NotAvailableShift
            date={date}
            shiftCourierList={shiftCourierList}
            setShiftCourierList={setShiftCourierList}
            courierName={courierName}
            courierId={courierId}
            wishList={wishList}
            region={region}
          />
        )}
        {isShowNotAvailableShift && wishList.length > 0 && (
          <NotAvailableShiftUnRegister
            shiftCourierList={shiftCourierList}
            setShiftCourierList={setShiftCourierList}
            courierId={courierId}
            courierName={courierName}
            shiftCourier={wishList[0]}
          />
        )}
      </div>
    );
  } else {
    return (
      <div className="weekdayForCourier">
        <Loader />
      </div>
    );
  }
}
